/**
 * MobileAccountDetail Component
 *
 * Mobile page for viewing and editing account details.
 * Route: /settings/portfolio/:accountId
 *
 * Follows MobileBrokerDetail pattern adapted for account domain:
 * URL param validation with archived deep-link (two-tick), actions popover
 * (rename/archive/restore), editable form with dirty tracking via
 * AccountsContext, save/discard footer, pending action pattern,
 * UnsavedChangesDialog, and conflict banner for cross-tab sync.
 *
 * Name is edited exclusively via AccountRenameModal (not inline).
 * Section selector splits content into Details, Broker & Fees, Risk, and Transactions sections.
 */

// 1. External dependencies
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MoreVertical, Globe, AlertTriangle } from 'lucide-react';

// 2. Workspace packages
import {
  Button,
  Checkbox,
  Chip,
  Input,
  Radio,
  RadioGroup,
  Select,
  SelectItem,
  Switch,
  TextArea,
  useDisclosure,
  Breadcrumbs,
  BreadcrumbItem,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Link,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
} from '@enfomentis/ui';

// 3. Internal utilities and constants
import { ACCOUNT_TYPE_OPTIONS } from '@/constants/accountType';
import { ASSET_CLASS_TYPE_DISPLAY_MAP } from '@/constants/assetClass';
import { CATEGORY_COLORS } from '@/constants/colors';
import { PROP_FIRM_OPTIONS, PROP_FIRM_ASSET_CLASS_OPTIONS, ACCOUNT_STAGE_OPTIONS } from '@/constants/propFirm';
import { CURRENCY_DATABASE } from '@/constants';
import { getCurrencyInfo } from '@/utils';
import { toDisplayValue } from '@/utils/moneyHelpers';
import { brokerService } from '@/services/BrokerService';
import type { AccountType } from '@/types/shared/AccountType';
import type { Broker } from '@/types/schema/Broker';

// 4. Shared components
import { CurrencyChooserModal } from '@/components/shared/CurrencyChooser/CurrencyChooserModal';
import { UnsavedChangesDialog } from '@/components/shared/UnsavedChangesDialog';

// 5. Domain components & context
import { AccountRenameModal } from '../components';
import { useAccounts } from '../context';
import type { AccountFormState } from '../context/AccountsContext';
import { MobileRiskSection } from '../Risk';
import { MobileTransactionsSection } from '../Transactions';

// =============================================================================
// Route Constants
// =============================================================================

const ACCOUNT_LIST_ROUTE = '/settings/portfolio';
const SETTINGS_ROUTE = '/settings';

// =============================================================================
// Types
// =============================================================================

/** Structured navigation target for pending navigation */
interface PendingNav {
  to: string;
  replace?: boolean;
}

/** Mobile section selector keys */
type MobileAccountSection = 'details' | 'broker-fees' | 'risk' | 'transactions';


// =============================================================================
// Component
// =============================================================================

export const MobileAccountDetail: React.FC = () => {
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();
  const unsavedChangesDialog = useDisclosure();
  const renameModal = useDisclosure();

  // Pending navigation target when showing unsaved changes dialog
  const [pendingNavigation, setPendingNavigation] = useState<PendingNav | null>(null);

  // Pending action when archive/restore is attempted while dirty
  const [pendingAction, setPendingAction] = useState<'archive' | 'restore' | null>(null);

  // Actions popover state
  const [isActionsPopoverOpen, setIsActionsPopoverOpen] = useState(false);

  // Section selector
  const [activeSection, setActiveSection] = useState<MobileAccountSection>('details');

  // Currency chooser modal state
  const [showCurrencyChooser, setShowCurrencyChooser] = useState(false);

  // Restore-broker confirmation modal
  const [isRestoreBrokerModalOpen, setIsRestoreBrokerModalOpen] = useState(false);
  const [pendingRestoreBrokerInfo, setPendingRestoreBrokerInfo] = useState<{ brokerId: string; brokerName: string } | null>(null);

  // Tag input
  const [tagInput, setTagInput] = useState('');

  // Color picker — custom hex state
  const [customHex, setCustomHex] = useState('');
  const [isCustomColor, setIsCustomColor] = useState(false);

  // Archived broker fallback
  const [linkedBroker, setLinkedBroker] = useState<Broker | null>(null);

  // Context
  const {
    accounts,
    loading,
    selectedAccount,
    selectedAccountId,
    selectAccount,
    showArchived,
    setShowArchived,
    formState,
    setFormState,
    isDirty,
    isSaving,
    saveSelectedAccount,
    discardSelectedAccountChanges,
    renameSelectedAccount,
    showConflictBanner,
    acceptExternalChanges,
    keepEditing,
    activeBrokers,
    archiveAccount,
    restoreAccount,
    checkRestoreBlocked,
    restoreLinkedBrokerAndAccount,
    accountRisk,
    riskLoading,
    ensureRiskLoaded,
  } = useAccounts();

  // ===========================================================================
  // URL Param Validation & Context Sync
  // ===========================================================================

  useEffect(() => {
    // Wait for accounts to load before any validation
    if (loading) {
      return;
    }

    if (!accountId) {
      navigate(ACCOUNT_LIST_ROUTE, { replace: true });
      return;
    }

    // Validate against full accounts array (not filtered)
    const account = accounts.find((a) => a.id === accountId);

    if (!account) {
      // Account not found — redirect to list
      navigate(ACCOUNT_LIST_ROUTE, { replace: true });
      return;
    }

    // For archived deep-links: toggle showArchived first, then return
    // from this effect tick to let state settle before selecting
    if (account.status === 'archived' && !showArchived) {
      setShowArchived(true);
      return;
    }

    // Sync to context if not already selected
    if (selectedAccountId !== accountId) {
      selectAccount(accountId);
    }
  }, [accountId, accounts, loading, selectedAccountId, showArchived, setShowArchived, selectAccount, navigate]);

  // ===========================================================================
  // Baseline Reset Effect
  // ===========================================================================

  useEffect(() => {
    // Reset UI state
    setTagInput('');
    setPendingNavigation(null);
    setPendingAction(null);
    setActiveSection('details');

    // Close transient overlays
    setIsActionsPopoverOpen(false);
    renameModal.onClose();
    setShowCurrencyChooser(false);
  }, [selectedAccountId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync color picker UI from authoritative formState.color
  // Single source of truth — covers selection changes, discard, accept, and external sync
  useEffect(() => {
    const color = formState.color;
    const isPreset = CATEGORY_COLORS.some((c) => c.value === color);
    if (color && !isPreset) {
      setCustomHex(color);
      setIsCustomColor(true);
    } else {
      setCustomHex('');
      setIsCustomColor(false);
    }
  }, [formState.color]);

  // ===========================================================================
  // Archived Broker Fallback Effect
  // ===========================================================================

  useEffect(() => {
    if (!formState.brokerId) {
      setLinkedBroker(null);
      return;
    }
    const inActive = activeBrokers.some((b) => b.id === formState.brokerId);
    if (inActive) {
      setLinkedBroker(null);
      return;
    }
    let cancelled = false;
    brokerService.get(formState.brokerId).then((broker) => {
      if (!cancelled) {
        setLinkedBroker(broker ?? null);
      }
    });
    return () => { cancelled = true; };
  }, [formState.brokerId, activeBrokers]);

  // ===========================================================================
  // Risk Lazy-Load Effect
  // ===========================================================================

  useEffect(() => {
    if (activeSection === 'risk') {
      void ensureRiskLoaded();
    }
  }, [activeSection, ensureRiskLoaded]);

  // ===========================================================================
  // Derived Values
  // ===========================================================================

  const isFormValid = formState.brokerId !== '';

  // Broker currency derivation
  const activeBrokerMatch = activeBrokers.find((b) => b.id === formState.brokerId);
  const selectedBrokerForForm = activeBrokerMatch ?? linkedBroker;
  const brokerCurrency = selectedBrokerForForm?.feeMetadata?.defaultAccountCurrency ?? 'USD';
  const currencyCode = formState.baseCurrency || brokerCurrency;
  const currencyInfo = getCurrencyInfo(currencyCode, CURRENCY_DATABASE);
  const isValidCustomHex = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(customHex.trim());

  // Fee schedule derived values (mirrors AccountsPage.tsx desktop logic)
  const {
    feeScheduleConfigs,
    feeScheduleAssetGroups,
    feeSchedulePlatform,
    feeSchedulePrecision,
    feeScheduleSymbol,
    hasNonZeroPlatformFees,
    hasOvernightDefault,
    hasFxDefault,
    isFeeScheduleEmpty,
  } = useMemo(() => {
    const configs = selectedBrokerForForm?.feeConfigurations.filter(c => c.status === 'active') ?? [];
    const assetGroups = configs.reduce<Record<string, number>>((acc, cfg) => {
      const key = cfg.category?.assetClass ?? 'other';
      acc[key] = (acc[key] ?? 0) + 1;
      return acc;
    }, {});
    const platform = selectedBrokerForForm?.feeMetadata?.platformFees;
    const currCode = selectedBrokerForForm?.feeMetadata?.defaultAccountCurrency ?? 'USD';
    const currInfo = getCurrencyInfo(currCode, CURRENCY_DATABASE);
    const precision = currInfo?.minorUnit ?? selectedBrokerForForm?.feeMetadata?.minimumBalance?.precision ?? 2;
    const symbol = currInfo?.symbol ?? currCode;
    const nonZeroPlatform = !!(platform && (
      (platform.monthlyFee !== undefined && platform.monthlyFee !== 0) ||
      (platform.dataFees !== undefined && platform.dataFees !== 0) ||
      (platform.inactivityFee !== undefined && platform.inactivityFee !== 0)
    ));
    const overnightDefault = !!selectedBrokerForForm?.brokerOvernightFinancingDefault;
    const fxDefault = !!selectedBrokerForForm?.brokerCurrencyConversionDefault;
    const empty = !!selectedBrokerForForm && configs.length === 0
      && !nonZeroPlatform && !overnightDefault && !fxDefault;
    return {
      feeScheduleConfigs: configs,
      feeScheduleAssetGroups: assetGroups,
      feeSchedulePlatform: platform,
      feeSchedulePrecision: precision,
      feeScheduleSymbol: symbol,
      hasNonZeroPlatformFees: nonZeroPlatform,
      hasOvernightDefault: overnightDefault,
      hasFxDefault: fxDefault,
      isFeeScheduleEmpty: empty,
    };
  }, [selectedBrokerForForm]);

  // ===========================================================================
  // Form Field Handlers
  // ===========================================================================

  const handleFormFieldChange = useCallback(<K extends keyof AccountFormState>(
    field: K,
    value: AccountFormState[K],
  ): void => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  }, [setFormState]);

  const handleAccountTypeChange = useCallback((value: AccountType): void => {
    setFormState((prev) => ({
      ...prev,
      accountType: value,
      ...(value !== 'other' ? { customAccountTypeName: '' } : {}),
      ...(value !== 'prop' ? {
        propFirmDetails: {
          firmName: 'apex' as const,
          customFirmName: '',
          assetClass: 'futures' as const,
          customAssetClass: '',
          accountStage: 'evaluation' as const,
        },
      } : {}),
    }));
  }, [setFormState]);

  const handlePropFirmChange = useCallback(<K extends keyof AccountFormState['propFirmDetails']>(
    field: K,
    value: AccountFormState['propFirmDetails'][K],
  ): void => {
    setFormState((prev) => ({
      ...prev,
      propFirmDetails: { ...prev.propFirmDetails, [field]: value },
    }));
  }, [setFormState]);

  const handleFeePreferenceChange = useCallback((
    field: keyof AccountFormState['feePreferences'],
    value: boolean,
  ): void => {
    setFormState((prev) => ({
      ...prev,
      feePreferences: { ...prev.feePreferences, [field]: value },
    }));
  }, [setFormState]);

  const handleColorChange = useCallback((value: string): void => {
    if (value === '__custom__') {
      setIsCustomColor(true);
    } else {
      setIsCustomColor(false);
      setCustomHex('');
      handleFormFieldChange('color', value);
    }
  }, [handleFormFieldChange]);

  const handleCustomHexChange = useCallback((value: string): void => {
    setCustomHex(value);
    const trimmed = value.trim();
    if (/^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(trimmed)) {
      const normalized = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
      handleFormFieldChange('color', normalized);
    }
  }, [handleFormFieldChange]);

  const handleCurrencySelect = useCallback((code: string): void => {
    handleFormFieldChange('baseCurrency', code);
    setShowCurrencyChooser(false);
  }, [handleFormFieldChange]);

  const addTag = useCallback((value: string): void => {
    const trimmed = value.trim();
    if (!trimmed) return;
    const normalized = trimmed.toLowerCase();
    setFormState((prev) => {
      if (prev.tags.some((t) => t.toLowerCase() === normalized)) return prev;
      return { ...prev, tags: [...prev.tags, trimmed] };
    });
    setTagInput('');
  }, [setFormState]);

  const removeTag = useCallback((tag: string): void => {
    setFormState((prev) => ({
      ...prev,
      tags: prev.tags.filter((t) => t !== tag),
    }));
  }, [setFormState]);

  const handleTagKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(tagInput);
    }
  }, [addTag, tagInput]);

  // ===========================================================================
  // Navigation Handlers
  // ===========================================================================

  const handleNavigateWithDirtyCheck = useCallback(
    (target: string, options?: { replace?: boolean }): void => {
      if (isSaving) return;
      if (isDirty) {
        setPendingNavigation({ to: target, ...(options?.replace ? { replace: true } : {}) });
        unsavedChangesDialog.onOpen();
      } else {
        navigate(target, options);
      }
    },
    [isSaving, isDirty, navigate, unsavedChangesDialog],
  );

  const handleBack = useCallback((): void => {
    handleNavigateWithDirtyCheck(ACCOUNT_LIST_ROUTE);
  }, [handleNavigateWithDirtyCheck]);

  const handleNavigateToSettings = useCallback((): void => {
    handleNavigateWithDirtyCheck(SETTINGS_ROUTE);
  }, [handleNavigateWithDirtyCheck]);

  // ===========================================================================
  // Save Handler
  // ===========================================================================


  // ===========================================================================
  // Discard Handler
  // ===========================================================================

  const handleDiscardChanges = useCallback(async (): Promise<void> => {
    await discardSelectedAccountChanges();
    setTagInput('');
  }, [discardSelectedAccountChanges]);

  /** Dialog discard — discards form then executes pending navigation or action */
  const handleDiscard = useCallback(async (): Promise<void> => {
    await handleDiscardChanges();
    unsavedChangesDialog.onClose();
    if (pendingNavigation) {
      navigate(pendingNavigation.to, pendingNavigation.replace ? { replace: true } : undefined);
      setPendingNavigation(null);
    } else if (pendingAction === 'archive') {
      setPendingAction(null);
      void archiveAccount().then((success) => {
        if (success) navigate(ACCOUNT_LIST_ROUTE, { replace: true });
      });
    } else if (pendingAction === 'restore') {
      setPendingAction(null);
      const result = await checkRestoreBlocked();
      if (result.blocked) {
        setPendingRestoreBrokerInfo({ brokerId: result.brokerId, brokerName: result.brokerName });
        setIsRestoreBrokerModalOpen(true);
        return;
      }
      void restoreAccount();
    }
  }, [handleDiscardChanges, unsavedChangesDialog, pendingNavigation, navigate, pendingAction, archiveAccount, restoreAccount, checkRestoreBlocked]);

  // ===========================================================================
  // Actions Handlers (Title Row Popover)
  // ===========================================================================

  const handleRenameClick = useCallback((): void => {
    setIsActionsPopoverOpen(false);
    renameModal.onOpen();
  }, [renameModal]);

  const handleArchiveClick = useCallback((): void => {
    setIsActionsPopoverOpen(false);
    if (isDirty) {
      setPendingAction('archive');
      unsavedChangesDialog.onOpen();
      return;
    }
    void archiveAccount().then((success) => {
      if (success) navigate(ACCOUNT_LIST_ROUTE, { replace: true });
    });
  }, [isDirty, archiveAccount, navigate, unsavedChangesDialog]);

  const handleRestoreClick = useCallback(async (): Promise<void> => {
    setIsActionsPopoverOpen(false);
    if (isDirty) {
      setPendingAction('restore');
      unsavedChangesDialog.onOpen();
      return;
    }
    const result = await checkRestoreBlocked();
    if (result.blocked) {
      setPendingRestoreBrokerInfo({ brokerId: result.brokerId, brokerName: result.brokerName });
      setIsRestoreBrokerModalOpen(true);
      return;
    }
    void restoreAccount();
  }, [isDirty, checkRestoreBlocked, restoreAccount, unsavedChangesDialog]);

  const handleRestoreBrokerAndAccount = useCallback(async (): Promise<void> => {
    setIsRestoreBrokerModalOpen(false);
    setPendingRestoreBrokerInfo(null);
    await restoreLinkedBrokerAndAccount();
  }, [restoreLinkedBrokerAndAccount]);

  const handleRenameSubmit = useCallback(async (newName: string): Promise<boolean> => {
    return renameSelectedAccount(newName);
  }, [renameSelectedAccount]);

  // ===========================================================================
  // Loading / Not-Found Guard
  // ===========================================================================

  // Guard: prevent rendering stale account during URL-param transitions
  if (!selectedAccount || selectedAccount.id !== accountId) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-text-secondary">Loading...</p>
      </div>
    );
  }

  // ===========================================================================
  // Render
  // ===========================================================================

  return (
    <>
      <Card radius="none" shadow="none" className="h-full flex flex-col">
        {/* Header — Breadcrumb Navigation + Page Title + Actions */}
        <CardHeader className="flex-col items-start pt-1 pb-1 border-b border-default-200">
          <div className="flex items-center">
            <Breadcrumbs variant="light" size="sm">
              <BreadcrumbItem
                onPress={handleNavigateToSettings}
                className="text-text-secondary hover:text-text-primary"
                isCurrent={false}
              >
                Settings
              </BreadcrumbItem>
              <BreadcrumbItem
                onPress={handleBack}
                className="text-text-secondary hover:text-text-primary"
                isCurrent={false}
              >
                Portfolio
              </BreadcrumbItem>
            </Breadcrumbs>
          </div>
          <div className="flex items-center justify-between w-full mt-1">
            <h1 className="text-lg font-semibold text-text-primary min-w-0">
              {selectedAccount.name}
            </h1>
            <div className="flex items-center gap-2 shrink-0">
              {selectedAccount.status === 'archived' && (
                <span className="text-xs text-text-tertiary">Archived</span>
              )}
              {/* Account Actions Popover */}
              <Popover
                isOpen={isActionsPopoverOpen}
                onOpenChange={setIsActionsPopoverOpen}
                placement="bottom-end"
              >
                <PopoverTrigger>
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    aria-label="Account actions"
                    className="opacity-60 hover:opacity-100"
                  >
                    <MoreVertical size={16} />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-1">
                  <div className="flex flex-col min-w-40">
                    <Button
                      size="md"
                      variant="light"
                      className="justify-start"
                      isDisabled={isSaving}
                      onPress={handleRenameClick}
                    >
                      Rename
                    </Button>
                    {selectedAccount.status !== 'archived' && (
                      <Button
                        size="md"
                        variant="light"
                        className="justify-start text-warning-600"
                        isDisabled={isSaving}
                        onPress={handleArchiveClick}
                      >
                        Archive
                      </Button>
                    )}
                    {selectedAccount.status === 'archived' && (
                      <Button
                        size="md"
                        variant="light"
                        className="justify-start text-success-600"
                        isDisabled={isSaving}
                        onPress={handleRestoreClick}
                      >
                        Restore
                      </Button>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </CardHeader>

        {/* Conflict Banner */}
        {showConflictBanner && (
          <div className="mx-4 mt-4 rounded-lg bg-warning-50 border border-warning-200 p-3">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-warning-600 shrink-0" />
              <span className="text-sm text-warning-700">Account updated in another tab</span>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="flat" onPress={keepEditing}>
                Keep editing
              </Button>
              <Button size="sm" color="warning" onPress={acceptExternalChanges}>
                Accept changes
              </Button>
            </div>
          </div>
        )}

        {/* Section Selector — sub-header style (ReferenceDataPage pattern) */}
        <div className="px-1 pt-1">
          <Select
            aria-label="Account section"
            selectedKeys={[activeSection]}
            onSelectionChange={(keys) => {
              if (keys === 'all') return;
              const val = Array.from(keys)[0] as MobileAccountSection | undefined;
              if (val) setActiveSection(val);
            }}
            disallowEmptySelection
            className="w-full"
            classNames={{
              trigger: 'bg-transparent shadow-none data-[hover=true]:bg-default-50 px-0 min-h-unit-8',
              value: '!text-text-primary text-lg',
            }}
            listboxProps={{
              itemClasses: {
                base: [
                  'data-[hover=true]:!text-primary data-[hover=true]:!bg-transparent',
                  'data-[selectable=true]:focus:!bg-transparent data-[selectable=true]:focus:!text-primary',
                  'data-[selected=true]:!text-primary data-[selected=true]:!bg-transparent',
                ].join(' '),
              },
            }}
          >
            <SelectItem key="details" textValue="Details">Details</SelectItem>
            <SelectItem key="broker-fees" textValue="Broker & Fees">Broker &amp; Fees</SelectItem>
            <SelectItem key="risk" textValue="Risk Profiles">Risk Profiles</SelectItem>
            <SelectItem key="transactions" textValue="Transactions">Transactions</SelectItem>
          </Select>
        </div>

        {/* Content — layout switches for transactions (flex fill) vs other sections (scrollable form) */}
        <CardBody className={activeSection === 'transactions'
          ? "flex-1 min-h-0 flex flex-col px-1 py-0"
          : "flex-1 overflow-y-auto px-1 py-0"
        }>
          {/* Transactions section renders directly in outer CardBody for unbroken flex chain */}
          {activeSection === 'transactions' && <MobileTransactionsSection />}

          {/* Other sections wrapped in inner Card for form spacing */}
          {activeSection !== 'transactions' && (
          <Card shadow="none" radius="none" className="px-1 py-0 w-full ">
            <CardBody className="space-y-4 px-0">

              {/* =============== Details Section =============== */}
              {activeSection === 'details' && (
                <>
                  {/* --- Core Details --- */}

                  {/* Account Type */}
                  <Select
                    label="Account Type"
                    labelPlacement="outside"
                    selectedKeys={[formState.accountType]}
                    onSelectionChange={(keys) => {
                      if (keys === 'all') return;
                      const val = Array.from(keys)[0] as AccountType | undefined;
                      if (val) handleAccountTypeChange(val);
                    }}
                    isRequired
                  >
                    {ACCOUNT_TYPE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} textValue={opt.label}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </Select>

                  {/* Custom Type Name (conditional) */}
                  {formState.accountType === 'other' && (
                    <Input
                      label="Custom Type Name"
                      labelPlacement="outside"
                      placeholder="Enter custom account type..."
                      value={formState.customAccountTypeName}
                      onValueChange={(val) => handleFormFieldChange('customAccountTypeName', val)}
                      className="[&_input]:text-base"
                    />
                  )}

                  {/* Account Number */}
                  <Input
                    label="Account Number"
                    labelPlacement="outside"
                    placeholder="Optional account number"
                    value={formState.accountNumber}
                    onValueChange={(val) => handleFormFieldChange('accountNumber', val)}
                    className="[&_input]:text-base"
                  />

                  {/* Description */}
                  <TextArea
                    label="Description"
                    labelPlacement="outside-top"
                    placeholder="Optional description or notes..."
                    value={formState.description}
                    onValueChange={(val) => handleFormFieldChange('description', val)}
                    minRows={2}
                    maxRows={4}
                    className="[&_textarea]:text-base"
                  />

                  {/* --- Prop Firm Details (conditional) --- */}
                  {formState.accountType === 'prop' && (
                    <>
                      <div className="border-t border-default-200" />
                      <div className="space-y-4">
                        <p className="text-sm font-semibold text-text-primary">Prop Firm Details</p>

                        <Select
                          label="Firm Name"
                          labelPlacement="outside"
                          selectedKeys={[formState.propFirmDetails.firmName]}
                          onSelectionChange={(keys) => {
                            if (keys === 'all') return;
                            const val = Array.from(keys)[0] as typeof formState.propFirmDetails.firmName | undefined;
                            if (val) {
                              handlePropFirmChange('firmName', val);
                              if (val !== 'other') handlePropFirmChange('customFirmName', '');
                            }
                          }}
                        >
                          {PROP_FIRM_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} textValue={opt.label}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </Select>

                        {formState.propFirmDetails.firmName === 'other' && (
                          <Input
                            label="Custom Firm Name"
                            labelPlacement="outside"
                            placeholder="Enter prop firm name..."
                            value={formState.propFirmDetails.customFirmName}
                            onValueChange={(val) => handlePropFirmChange('customFirmName', val)}
                            className="[&_input]:text-base"
                          />
                        )}

                        <Select
                          label="Asset Class"
                          labelPlacement="outside"
                          selectedKeys={[formState.propFirmDetails.assetClass]}
                          onSelectionChange={(keys) => {
                            if (keys === 'all') return;
                            const val = Array.from(keys)[0] as typeof formState.propFirmDetails.assetClass | undefined;
                            if (val) {
                              handlePropFirmChange('assetClass', val);
                              if (val !== 'other') handlePropFirmChange('customAssetClass', '');
                            }
                          }}
                        >
                          {PROP_FIRM_ASSET_CLASS_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} textValue={opt.label}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </Select>

                        {formState.propFirmDetails.assetClass === 'other' && (
                          <Input
                            label="Custom Asset Class"
                            labelPlacement="outside"
                            placeholder="Enter asset class..."
                            value={formState.propFirmDetails.customAssetClass}
                            onValueChange={(val) => handlePropFirmChange('customAssetClass', val)}
                            className="[&_input]:text-base"
                          />
                        )}

                        <Select
                          label="Account Stage"
                          labelPlacement="outside"
                          selectedKeys={[formState.propFirmDetails.accountStage]}
                          onSelectionChange={(keys) => {
                            if (keys === 'all') return;
                            const val = Array.from(keys)[0] as typeof formState.propFirmDetails.accountStage | undefined;
                            if (val) handlePropFirmChange('accountStage', val);
                          }}
                        >
                          {ACCOUNT_STAGE_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} textValue={opt.label}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </Select>
                      </div>
                    </>
                  )}

                  {/* --- Organization --- */}
                  <div className="border-t border-default-200" />
                  <div className="space-y-4">
                    <p className="text-sm font-semibold text-text-primary">Organization</p>

                    {/* Color Picker */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-text-secondary">Color</label>
                      <RadioGroup
                        value={isCustomColor ? '__custom__' : formState.color}
                        onValueChange={handleColorChange}
                        orientation="horizontal"
                        classNames={{ wrapper: 'gap-3 flex-wrap' }}
                      >
                        {CATEGORY_COLORS.map((color) => (
                          <Radio
                            key={color.value}
                            value={color.value}
                            classNames={{ base: 'm-0' }}
                          >
                            <div className="flex items-center gap-2">
                              <div
                                className="w-6 h-6 rounded-full border-2 border-default-300"
                                style={{ backgroundColor: color.value }}
                              />
                              <span className="text-sm">{color.name}</span>
                            </div>
                          </Radio>
                        ))}
                        <Radio
                          value="__custom__"
                          classNames={{ base: 'm-0' }}
                        >
                          <div className="flex items-center gap-2">
                            <div
                              className="w-6 h-6 rounded-full border-2 border-default-300"
                              style={{
                                backgroundColor: isCustomColor && isValidCustomHex
                                  ? (customHex.trim().startsWith('#') ? customHex.trim() : `#${customHex.trim()}`)
                                  : '#cccccc',
                              }}
                            />
                            <span className="text-sm">Custom</span>
                          </div>
                        </Radio>
                      </RadioGroup>
                      {isCustomColor && (
                        <div className="flex items-center gap-3 mt-2">
                          <Input
                            label="Hex Color"
                            placeholder="#ff5500"
                            value={customHex}
                            onValueChange={handleCustomHexChange}
                            variant="bordered"
                            size="sm"
                            className="max-w-40"
                            isInvalid={customHex.length > 0 && !isValidCustomHex}
                            errorMessage={
                              customHex.length > 0 && !isValidCustomHex
                                ? 'Invalid hex color'
                                : undefined
                            }
                          />
                          {isValidCustomHex && (
                            <div
                              className="w-8 h-8 rounded-full border-2 border-default-300 shrink-0"
                              style={{ backgroundColor: customHex.trim().startsWith('#') ? customHex.trim() : `#${customHex.trim()}` }}
                            />
                          )}
                        </div>
                      )}
                    </div>

                    {/* Tags */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-text-secondary">Tags</label>
                      <Input
                        placeholder="Add tag and press Enter..."
                        value={tagInput}
                        onValueChange={setTagInput}
                        onKeyDown={handleTagKeyDown}
                        size="sm"
                        className="[&_input]:text-base"
                      />
                      {formState.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {formState.tags.map((tag) => (
                            <Chip
                              key={tag}
                              size="sm"
                              variant="flat"
                              onClose={() => removeTag(tag)}
                            >
                              {tag}
                            </Chip>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* =============== Broker & Fees Section =============== */}
              {activeSection === 'broker-fees' && (
                <>
                  {/* --- Linked Broker --- */}
                  <div className="space-y-4">
                    <p className="text-sm font-semibold text-text-primary">Linked Broker</p>

                    {/* Broker Select */}
                    <Select
                      label="Broker"
                      labelPlacement="outside"
                      selectedKeys={formState.brokerId && (activeBrokerMatch || linkedBroker) ? [formState.brokerId] : []}
                      onSelectionChange={(keys) => {
                        if (keys === 'all') return;
                        const val = Array.from(keys)[0] as string | undefined;
                        if (val) handleFormFieldChange('brokerId', val);
                      }}
                      isRequired
                      {...(linkedBroker ? { disabledKeys: [linkedBroker.id] } : {})}
                    >
                      {[
                        ...(linkedBroker
                          ? [
                              <SelectItem key={linkedBroker.id} textValue={`${linkedBroker.name} (Archived)`}>
                                {linkedBroker.name} (Archived)
                              </SelectItem>,
                            ]
                          : []),
                        ...activeBrokers.map((broker) => (
                          <SelectItem key={broker.id} textValue={broker.name}>
                            {broker.name}
                          </SelectItem>
                        )),
                      ]}
                    </Select>

                    {/* Broker currency info */}
                    {selectedBrokerForForm && !linkedBroker && (
                      <p className="text-xs text-text-tertiary -mt-2">
                        {selectedBrokerForForm.name} &middot; Default: {brokerCurrency}
                      </p>
                    )}

                    {/* Currency Override */}
                    <div className="min-w-0 space-y-2">
                      <Switch
                        size="sm"
                        isSelected={formState.baseCurrency !== ''}
                        isDisabled={!formState.brokerId}
                        onValueChange={(on) => {
                          if (on) {
                            handleFormFieldChange('baseCurrency', brokerCurrency);
                          } else {
                            handleFormFieldChange('baseCurrency', '');
                          }
                        }}
                      >
                        Override broker default currency
                      </Switch>
                      {formState.baseCurrency ? (
                        <Button
                          variant="bordered"
                          className="w-full justify-between text-base font-normal border-default-200"
                          endContent={<Globe className="w-4 h-4 text-text-tertiary shrink-0" />}
                          onPress={() => setShowCurrencyChooser(true)}
                          isDisabled={isSaving}
                        >
                          {currencyInfo ? (
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{currencyInfo.symbol}</span>
                              <span>{currencyInfo.name}</span>
                              <span className="text-text-secondary">({currencyCode})</span>
                            </div>
                          ) : (
                            <span>{currencyCode}</span>
                          )}
                        </Button>
                      ) : (
                        <p className="text-xs text-text-tertiary">
                          {formState.brokerId
                            ? `Using broker default: ${brokerCurrency}${currencyInfo ? ` — ${currencyInfo.name}` : ''}`
                            : 'Select a broker first'}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* --- Fee Tracking --- */}
                  <div className="border-t border-default-200" />
                  <div className="space-y-3">
                    <p className="text-sm font-semibold text-text-primary">Fee Tracking</p>
                    <Checkbox
                      isSelected={formState.feePreferences.includeFeeInPnL}
                      onValueChange={(val) => handleFeePreferenceChange('includeFeeInPnL', val)}
                    >
                      Include fees in P&amp;L calculations
                    </Checkbox>
                    <Checkbox
                      isSelected={formState.feePreferences.promptFeeVerification}
                      onValueChange={(val) => handleFeePreferenceChange('promptFeeVerification', val)}
                    >
                      Prompt fee verification on new instruments
                    </Checkbox>
                    <Checkbox
                      isSelected={formState.feePreferences.trackOvernightFinancing}
                      onValueChange={(val) => handleFeePreferenceChange('trackOvernightFinancing', val)}
                    >
                      Track overnight financing costs
                    </Checkbox>
                    <Checkbox
                      isSelected={formState.feePreferences.trackCurrencyConversion}
                      onValueChange={(val) => handleFeePreferenceChange('trackCurrencyConversion', val)}
                    >
                      Track currency conversion costs
                    </Checkbox>
                  </div>

                  {/* --- Fee Schedule (Read-Only) --- */}
                  <div className="border-t border-default-200" />
                  <div className="space-y-3">
                    <p className="text-sm font-semibold text-text-primary">Fee Schedule</p>
                    {selectedBrokerForForm ? (
                      isFeeScheduleEmpty ? (
                        <p className="text-sm text-text-tertiary py-4 text-center">
                          No fees configured for this broker
                        </p>
                      ) : (
                        <>
                          <p className="text-sm text-text-secondary">
                            {feeScheduleConfigs.length} {feeScheduleConfigs.length === 1 ? 'configuration' : 'configurations'} across{' '}
                            {Object.keys(feeScheduleAssetGroups).length} {Object.keys(feeScheduleAssetGroups).length === 1 ? 'asset class' : 'asset classes'}
                          </p>
                          {Object.keys(feeScheduleAssetGroups).length > 0 && (
                            <div className="space-y-1">
                              {Object.entries(feeScheduleAssetGroups)
                                .sort(([a], [b]) => a.localeCompare(b))
                                .map(([key, count]) => (
                                  <p key={key} className="text-xs text-text-tertiary">
                                    {key === 'other' ? 'Other' : ASSET_CLASS_TYPE_DISPLAY_MAP[key as keyof typeof ASSET_CLASS_TYPE_DISPLAY_MAP]}:{' '}
                                    {count} {count === 1 ? 'config' : 'configs'}
                                  </p>
                                ))}
                            </div>
                          )}
                          <div className="text-xs text-text-tertiary">
                            <span className="font-medium text-text-secondary">Platform Fees: </span>
                            {hasNonZeroPlatformFees ? (
                              [
                                feeSchedulePlatform?.monthlyFee ? `Monthly: ${feeScheduleSymbol}${(toDisplayValue(feeSchedulePlatform.monthlyFee, feeSchedulePrecision) ?? 0).toFixed(feeSchedulePrecision)}` : null,
                                feeSchedulePlatform?.dataFees ? `Data: ${feeScheduleSymbol}${(toDisplayValue(feeSchedulePlatform.dataFees, feeSchedulePrecision) ?? 0).toFixed(feeSchedulePrecision)}` : null,
                                feeSchedulePlatform?.inactivityFee ? `Inactivity: ${feeScheduleSymbol}${(toDisplayValue(feeSchedulePlatform.inactivityFee, feeSchedulePrecision) ?? 0).toFixed(feeSchedulePrecision)}` : null,
                              ].filter(Boolean).join(' \u00b7 ')
                            ) : (
                              'None configured'
                            )}
                          </div>
                          <div className="text-xs text-text-tertiary">
                            <span className="font-medium text-text-secondary">Overnight Financing: </span>
                            {hasOvernightDefault ? 'Configured' : 'Not configured'}
                          </div>
                          <div className="text-xs text-text-tertiary">
                            <span className="font-medium text-text-secondary">Currency Conversion: </span>
                            {hasFxDefault ? 'Configured' : 'Not configured'}
                          </div>
                        </>
                      )
                    ) : (
                      <p className="text-sm text-text-tertiary py-4 text-center">
                        Select a broker to view fee schedule
                      </p>
                    )}
                  </div>

                  {/* --- View Broker Settings --- */}
                  {selectedBrokerForForm && (
                    <div className="border-t border-default-200" />
                  )}
                  {selectedBrokerForForm && (
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-text-tertiary">
                        View and manage fee configurations
                      </p>
                      <Link
                        onPress={() => handleNavigateWithDirtyCheck(`/settings/portfolio/reference-data/brokers/${formState.brokerId}`)}
                        showAnchorIcon
                        size="sm"
                        className="cursor-pointer shrink-0"
                      >
                        View Broker Settings
                      </Link>
                    </div>
                  )}
                </>
              )}

              {/* =============== Risk Profiles Section =============== */}
              {activeSection === 'risk' && (
                <>
                  {riskLoading || !accountRisk ? (
                    <div className="flex items-center justify-center py-12">
                      <Spinner size="lg" label="Loading risk profiles..." />
                    </div>
                  ) : (
                    <MobileRiskSection />
                  )}
                </>
              )}

            </CardBody>
          </Card>
          )}
        </CardBody>

        {/* Save/Discard Footer */}
        <CardFooter className="flex items-center justify-end pt-2 pb-0 border-t border-default-200">
          <div className="flex items-center gap-1">
            {isDirty && <span className="text-xs text-warning-600">Unsaved</span>}
            <Button
              variant="flat"
              size="sm"
              onPress={handleDiscardChanges}
              isDisabled={!isDirty || isSaving}
            >
              Discard
            </Button>
            <Button
              color="primary"
              size="sm"
              onPress={saveSelectedAccount}
              isDisabled={!isDirty || !isFormValid || isSaving}
              isLoading={isSaving}
            >
              Save
            </Button>
          </div>
        </CardFooter>
      </Card>

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        isOpen={unsavedChangesDialog.isOpen}
        onClose={() => {
          unsavedChangesDialog.onClose();
          setPendingNavigation(null);
          setPendingAction(null);
        }}
        onDiscard={handleDiscard}
      />

      {/* Account Rename Modal */}
      <AccountRenameModal
        isOpen={renameModal.isOpen}
        onClose={renameModal.onClose}
        currentName={selectedAccount.name}
        onSubmit={handleRenameSubmit}
      />

      {/* Currency Chooser Modal */}
      <CurrencyChooserModal
        isOpen={showCurrencyChooser}
        onClose={() => setShowCurrencyChooser(false)}
        selectedCurrency={formState.baseCurrency || brokerCurrency}
        onCurrencySelect={handleCurrencySelect}
        title="Account Currency"
      />

      {/* Restore Broker Confirmation Modal */}
      <Modal
        isOpen={isRestoreBrokerModalOpen}
        onClose={() => {
          setIsRestoreBrokerModalOpen(false);
          setPendingRestoreBrokerInfo(null);
        }}
        size="sm"
      >
        <ModalContent>
          <ModalHeader>Restore Linked Broker?</ModalHeader>
          <ModalBody>
            <p className="text-sm text-text-secondary">
              This account is linked to an archived broker
              {pendingRestoreBrokerInfo ? ` "${pendingRestoreBrokerInfo.brokerName}"` : ''}.
              Restore the broker as well?
            </p>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="flat"
              size="sm"
              onPress={() => {
                setIsRestoreBrokerModalOpen(false);
                setPendingRestoreBrokerInfo(null);
              }}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              size="sm"
              onPress={handleRestoreBrokerAndAccount}
            >
              Restore Both
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
